import { mutateRoutingBoms } from "@/api/routingBoms/useRoutingBomsQuery";
import { customFunctions } from "@/config/customFunction";
import { BomByOperationCreateForm } from "@/features/bomSearch/table/form/BomByOperationCreateForm";
import { BomByOperationUpdateForm } from "@/features/bomSearch/table/form/BomByOperationUpdateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { theme } from "@/styles/theme";
import { handleErrorResponse } from "@/utils/errorMessage";
import { setToLocaleString } from "@/utils/unitMark";
import styled from "@emotion/styled";
import { ActionIcon, Badge, Button, Flex, Table, Text, useMantineColorScheme } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentWorksInnerRoutingDataRoutingBomsInner,
  DefaultApiRoutingBomsRoutingBomIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { ItemsDetailForm } from "../../ItemsDetailForm";

const TableContainer = styled(Table)``;

const Thead = styled.thead`
  display: flex;
  width: 100%;
  align-items: flex-start;
  align-self: stretch;
`;

const Th = styled.th<{ width?: number }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}%` : "auto")};
  padding: 7px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const Tr = styled.tr`
  display: flex;
  width: 100%;
  align-items: center;
  height: 45px;
`;

const Tbody = styled.tbody`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  align-self: stretch;
`;

const Td = styled.td<{ width?: number }>`
  display: flex;
  width: ${(props) => (props.width ? `${props.width}%` : "auto")};
  height: 100%;
  padding: 7px 10px;
  align-items: flex-start;
  gap: 10px;
`;

const TextNumber = styled<any>(Text)`
  width: 100%;
`;

export const RoutingBomRow = (params: {
  routingCode?: string | undefined;
  routingBoms: AuthSignupPost201ResponseEquipmentWorksInnerRoutingDataRoutingBomsInner[];
}) => {
  const { routingBoms, routingCode } = params;
  const { openModal } = useModal();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { mutate: deleteMutate } = useMutation(
    (params: DefaultApiRoutingBomsRoutingBomIdDeleteRequest) =>
      mutateRoutingBoms.delete(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["items"]);
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("item.bom")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("item.bom") }),
          `${t("common.deleteFail", { item: `${t("item.bom")}` })}`,
          "red",
        );
      },
    },
  );

  const openEditModal = ({
    routingBom,
  }: {
    routingBom: AuthSignupPost201ResponseEquipmentWorksInnerRoutingDataRoutingBomsInner;
  }) => {
    openModal(
      <BomByOperationUpdateForm data={routingBom} />,
      null,
      `${t("common.edit", { item: `${t("item.bom")}` })}`,
    );
  };

  const openCreateModal = ({ routingCode }: { routingCode: string | undefined }) => {
    openModal(
      <BomByOperationCreateForm routingCode={routingCode} />,
      null,
      `${t("common.additional", { item: `${t("item.bom")}` })}`,
    );
  };

  const handleDeleteBom = ({ routingBomId }: { routingBomId: number | undefined }) => {
    if (routingBomId === undefined) return;
    deleteMutate({
      routingBomId: routingBomId,
    });
  };

  return (
    <TableContainer>
      <Thead>
        <Tr>
          <Th width={10}>
            <Text>{t("routing.sequence")}</Text>
          </Th>
          <Th width={20}>{t("item.materialItemcode")}</Th>
          <Th width={10}>{t("item.name")}</Th>
          <Th width={10}>{t("item.type")}</Th>
          <Th width={10}>{t("item.spec")}</Th>
          <Th width={10}>{t("item.category")}</Th>
          <Th width={10}>{t("inventory.quantity")}</Th>
          <Th width={10}>{t("item.unit")}</Th>
          {customFunctions.ADD_ROUTING_BOM_DESCRIPTION && (
            <Th width={15}>{t("routing.descript")}</Th>
          )}
          <Th width={10}>{t("common.action")}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {routingBoms &&
          routingBoms.map(
            (
              routingBom: AuthSignupPost201ResponseEquipmentWorksInnerRoutingDataRoutingBomsInner,
              i: number,
            ) =>
              routingBom.seq &&
              routingBom.itemCode &&
              routingBom.quantity && (
                <Tr key={i}>
                  <Td width={10}>
                    <Text w="100%" ta="right">
                      {routingBom.seq}
                    </Text>
                  </Td>
                  <Td width={20}>
                    <DetailLink
                      onClick={() =>
                        openModal(
                          <ItemsDetailForm itemCode={routingBom.itemCode as string} />,
                          null,
                          `${t("common.detail", { item: `${t("item.item")}` })}`,
                        )
                      }
                    >
                      {routingBom?.item?.name ?? routingBom?.itemName ?? "-"}({routingBom.itemCode})
                      {/* <IconLink /> */}
                      {/* <IconLinkPlus /> */}
                    </DetailLink>
                    {/* <NavLink
                    label={routingBom.itemCode}
                    icon={<IconLink size="1rem" stroke={1.5} />}
                  /> */}
                  </Td>
                  <Td width={10}>{routingBom?.item?.name ?? routingBom?.itemName ?? "-"}</Td>
                  <Td width={10}>
                    <Badge>{routingBom.item?.itemType ? t(routingBom.item?.itemType) : ""}</Badge>
                  </Td>
                  <Td width={10}>{routingBom.item?.spec}</Td>
                  <Td width={10}>
                    <Text>{routingBom.item?.itemCategoryName}</Text>
                  </Td>
                  <Td width={10}>
                    <TextNumber ta="right">
                      {setToLocaleString(routingBom.quantity ?? "")}
                    </TextNumber>
                  </Td>
                  <Td width={10}>{routingBom.item?.unit_text}</Td>
                  {customFunctions.ADD_ROUTING_BOM_DESCRIPTION && (
                    <Td width={10}>
                      <Text>{routingBom.description}</Text>
                    </Td>
                  )}
                  <Td width={10}>
                    <Flex justify="center" align="center" gap="xs">
                      <ActionIcon color="yellow" onClick={() => openEditModal({ routingBom })}>
                        <IconEdit />
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        onClick={() => handleDeleteBom({ routingBomId: routingBom.id })}
                      >
                        <IconTrash />
                      </ActionIcon>
                    </Flex>
                  </Td>
                </Tr>
              ),
          )}
        <Tr>
          <Td style={{ width: "100%", justifyContent: "center" }}>
            <Button
              leftIcon={<IconPlus />}
              variant={"subtle"}
              color={isDarkMode ? "dark" : "white"}
              onClick={() => openCreateModal({ routingCode })}
            >
              {t("common.newAdditional", { item: `${t("item.bom")}` })}
            </Button>
          </Td>
        </Tr>
      </Tbody>
    </TableContainer>
  );
};

const Link = styled.a`
  color: ${theme.colors?.blue?.[7]};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
`;
